/**
 * 解析 `GET /v1/staff/_id/{id}/contact-qr` 与上传成功体，归一为展示用 URL 与可选制作人名称。
 * 后端字段名可能存在差异，在此做多形态兼容。
 */

/** 归一化后的联系方式二维码数据 */
export type StaffContactQrNormalized = {
    /** 二维码图片绝对地址 */
    imageUrl: string;
    /** 展示用制作人名称（可选） */
    producerName?: string;
};

/**
 * 从接口返回体中提取图片 URL（尝试常见字段路径）。
 * @param obj - 对象层
 * @param keys - 优先顺序的字段名
 * @returns 首个形如 http(s) 的字符串，若无则 undefined
 */
function pickHttpUrl(obj: Record<string, unknown> | undefined, keys: string[]): string | undefined {
    if (!obj) {
        return undefined;
    }
    for (const key of keys) {
        const v = obj[key];
        if (typeof v === 'string' && /^https?:\/\//i.test(v.trim())) {
            return v.trim();
        }
    }
    return undefined;
}

/**
 * 解析 GET contact-qr 或 PATCH 成功响应体。
 * @param raw - 接口原始返回（可能为字符串 URL 或嵌套对象）
 * @returns 归一结果；无法识别时返回 null
 */
export function parseStaffContactQrResponse(raw: unknown): StaffContactQrNormalized | null {
    if (raw == null) {
        return null;
    }
    if (typeof raw === 'string' && /^https?:\/\//i.test(raw.trim())) {
        return { imageUrl: raw.trim() };
    }
    if (typeof raw !== 'object') {
        return null;
    }
    const root = raw as Record<string, unknown>;
    /** 兼容一层 data 包装 */
    const layer =
        root.data != null && typeof root.data === 'object' && !Array.isArray(root.data)
            ? (root.data as Record<string, unknown>)
            : root;

    const oss = layer.ossResult;
    const ossObj = oss != null && typeof oss === 'object' ? (oss as Record<string, unknown>) : undefined;

    const imageUrl =
        pickHttpUrl(layer, ['url', 'qr_url', 'contact_qr_url', 'image_url', 'contactQrUrl', 'qrUrl']) ||
        pickHttpUrl(ossObj, ['url']);

    if (!imageUrl) {
        return null;
    }

    const nameCandidates = [
        layer.producer_name,
        layer.producerName,
        layer.nickname,
        layer.display_name,
        layer.producer,
        layer.creator_name,
    ];
    const producerName = nameCandidates.find((x) => typeof x === 'string' && x.trim()) as string | undefined;

    return {
        imageUrl,
        producerName: producerName?.trim(),
    };
}
